import { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectedToggleValue } from '../../../../redux/canvasResolutionSlice';
import { useFlexRow } from '../hooks/useFlexRow';
import { useResponsiveWidth } from '../responsiveWidth';

type FlexRowContentProps = {
	children: ReactNode[];
};

const align = {
	left: 'justify-start',
	center: 'justify-center',
	right: 'justify-end',
	between: 'justify-between',
	around: 'justify-around',
};

function StretchChildrenWidth({ children }: FlexRowContentProps) {
	const {
		colGap,
		colSizing,
		blockClass,
		preserveLayoutOnMobile,
		preventHorizontalStretch,
		experimentalHideEmptyCols = false,
	} = useFlexRow();
	const [renderStyles, setRenderStyles] = useState({});
	const windowsWidth = useSelector(selectedToggleValue);
	const { cols, breakOnMobile } = useResponsiveWidth(children, {
		preserveLayoutOnMobile,
		hideEmptyCols: experimentalHideEmptyCols,
	});

	useEffect(() => {
		const render = () => {
			if (colSizing === 'auto') {
				return setRenderStyles({
					width: 'auto',
				});
			}

			if (typeof children.length === 'undefined') {
				return setRenderStyles({
					width: '100%',
				});
			}

			if (windowsWidth < 640 && !preserveLayoutOnMobile) {
				return setRenderStyles({
					width: '100%',
				});
			}

			return setRenderStyles({
				width: `${Math.round(100 / children.length)}%`,
			});
		};

		render();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [windowsWidth, preserveLayoutOnMobile, colSizing]);

	const renderClass = () => {
		return blockClass && preventHorizontalStretch
			? ''
			: `vtex-flex-layout-0-x-stretchChildrenWidth--${blockClass} stretchChildrenWidth--${blockClass}`;
	};

	if (typeof children.length === 'undefined' || children.length === 0) {
		return (
			<div
				className={`pr0 items-stretch ${preventHorizontalStretch ? '' : `vtex-flex-layout-0-x-stretchChildrenWidth stretchChildrenWidth`} flex ${renderClass()}`}
				style={renderStyles}
			>
				{children}
			</div>
		);
	}

	const isSizingAuto = colSizing === 'auto';
	return (
		<>
			{cols.map((col: any, index) => {
				return (
					<div
						key={'frc-' + index}
						className={`pr${index + 1 < children.length ? colGap : 0} items-stretch ${preventHorizontalStretch ? '' : `vtex-flex-layout-0-x-stretchChildrenWidth stretchChildrenWidth`}  ${col.width === 'grow' ? 'flex-grow-1' : ''}  flex`}
						style={{
							width:
								preventHorizontalStretch || (isSizingAuto && !col.hasDefinedWidth)
									? 'auto'
									: breakOnMobile
										? '100%'
										: col.width,
						}}
					>
						{col.element}
					</div>
				);
			})}
		</>
	);
}

export function FlexRowContent({ children }: FlexRowContentProps) {
	const windowsWidth = useSelector(selectedToggleValue);

	const {
		fullWidth,
		marginTop,
		marginBottom,
		paddingBottom,
		paddingTop,
		border,
		experimentalHideEmptyCols,
		borderWidth,
		borderColor,
		colSizing,
		blockClass,
		horizontalAlign,
		colJustify = 'between',
		preserveLayoutOnMobile,
		parent,
	} = useFlexRow();

	const { breakOnMobile } = useResponsiveWidth(children, {
		preserveLayoutOnMobile,
		hideEmptyCols: experimentalHideEmptyCols,
	});

	const getValue = (value: any) => {
		if (typeof value === 'object') {
			if (windowsWidth > 1024 && (value.desktop ?? false)) return value.desktop;
			if (windowsWidth > 640 && (value.tablet ?? false)) return value.tablet;
			return value.mobile;
		} else {
			return value;
		}
	};

	function renderTokens() {
		let justifyBetween = `${colSizing === 'auto' ? align[colJustify ?? 'between'] : align['left']}`;
		if (horizontalAlign) {
			justifyBetween = align[horizontalAlign];
		}
		const mt = `mt${getValue(marginTop) || 0}`;
		const mb = `mb${getValue(marginBottom) || 0}`;
		const pt = `pt${getValue(paddingTop) || 0}`;
		const pb = `pb${paddingBottom || 0}`;
		const bw = `${borderWidth ? `bw${borderWidth}` : ''}`;
		const bc = `${borderColor ? `b--${borderColor}` : ''}`;
		let b = '';

		if (typeof border === 'string') {
			b += `b${border.charAt(0).toLowerCase()}`;
		} else {
			if (border) b += border.map((b) => `b${b.charAt(0).toLowerCase()}`).join(' ');
		}
		return `${justifyBetween} ${mt} ${mb} ${pt} ${pb} ${b} ${bw} ${bc}`;
	}

	return (
		<>
			{fullWidth || parent == 'COL' || parent == 'ROW' ? (
				<div
					className={`${breakOnMobile ? 'flex-none flex-ns' : 'flex'} ${renderTokens()} vtex-flex-layout-0-x-flexRowContent flexRowContent vtex-flex-layout-0-x-flexRowContent--${blockClass} flexRowContent--${blockClass} items-stretch w-100`}
				>
					<StretchChildrenWidth>{children}</StretchChildrenWidth>
				</div>
			) : (
				<section className="container vtex-store-components-3-x-container ph3 ph5-m ph2-xl mw9 center ">
					<div
						className={`${breakOnMobile ? 'flex-none flex-ns' : 'flex'} ${renderTokens()} vtex-flex-layout-0-x-flexRowContent flexRowContent vtex-flex-layout-0-x-flexRowContent--${blockClass} flexRowContent--${blockClass} items-stretch w-100`}
					>
						<StretchChildrenWidth>{children}</StretchChildrenWidth>
					</div>
				</section>
			)}
		</>
	);
}
